// Pure gesture-arbitration helpers for the mobile calendar. Kept side-effect-free
// so the classification + boundary-transfer rules can be unit-tested directly.
//
// Priorities encoded here (see spec §4): a gesture is HORIZONTAL only when
// horizontal movement clearly exceeds vertical; among horizontal gestures a fast
// flick changes the DAY, a slow/controlled drag changes the SCALE. Small
// movements return null so taps and micro-moves never navigate.

export const RATIO = 1.3;        // horizontal must exceed vertical by this factor
export const MIN_DX = 40;        // px — below this, treat as tap / vertical
export const DAY_MIN_DX = 55;    // px — a day change needs a clear horizontal throw
export const FAST_V = 0.5;       // px/ms — at/above this (with distance) = fast swipe

// Returns { type: 'day'|'scale', dir: 'left'|'right' } or null.
export function classifyHorizontalGesture({ dx, dy, dt }) {
  const adx = Math.abs(dx);
  const ady = Math.abs(dy);
  if (adx < MIN_DX) return null;          // too short → tap / not horizontal enough
  if (adx <= ady * RATIO) return null;    // not clearly horizontal → vertical/diagonal
  const v = dt > 0 ? adx / dt : Infinity; // px per ms
  const fast = v >= FAST_V && adx >= DAY_MIN_DX;
  const dir = dx < 0 ? "left" : "right";
  return { type: fast ? "day" : "scale", dir };
}

// Day navigation: fast swipe LEFT → next day (+1), RIGHT → previous (-1).
export function dayDeltaForDir(dir) {
  return dir === "left" ? 1 : -1;
}

// Scale change over the ordered list [15,60,120,1440]: LEFT → next (coarser),
// RIGHT → previous (finer). Clamps at both ends (no looping).
export function nextScaleIndex(index, dir, length) {
  const delta = dir === "left" ? 1 : -1;
  return Math.min(Math.max(index + delta, 0), length - 1);
}

// Discrete window-step direction from a vertical delta measured as
// (startY - endY): finger moved UP (dy>0) → show later times (+1); DOWN → earlier (-1).
export function verticalStepDir(dy) {
  return dy > 0 ? 1 : -1;
}

// Boundary transfer: should a continued vertical drag hand off to the outer page?
// moveY = currentY - startY. Finger DOWN (moveY>0) wants earlier times (window up):
// transfer only if already at the TOP (!canUp). Finger UP wants later (window down):
// transfer only if already at the BOTTOM (!canDown).
export function shouldTransferToPage({ moveY, canUp, canDown }) {
  if (moveY > 0) return !canUp;
  if (moveY < 0) return !canDown;
  return false;
}
